<template>
  <h6 class="text-neutral-text-strong text-base/6 font-bold mt-16">
    Partner-specific settings
  </h6>
  <div v-if="treeData.length === 0" class="flex">
    <p class="text-neutral-text-weak text-base/6 font-normal mr-8">
      You don’t have any partners
    </p>
    <RouterLink to="/partners/invite" class="text-secondary-accent text-base/6">
      <span>Add Partner</span>
      <FontAwesomeIcon class="text-[11px] ml-4" :icon="['fak', 'open-link']" />
    </RouterLink>
  </div>
  <BittsTreeWithSearch
    v-else
    :node-data="treeData"
    @update="onUpdate"
    :is-disabled
  >
    <template #node-suffix="{ node }">
      <OfflineManagedPartnerLabel
        v-if="
          isPartnerOrgNode(node) &&
          (isManagedOfflinePartner(node.id) || isOfflinePartner(node.id))
        "
        :partner="
          partnersStore.getPartnerOrgByUuid(node.id as string) as Partner
        "
        :is-tag="true"
      />

      <BittsTooltip
        placement="bottom"
        v-if="isPartnerOrgNode(node) && node.isDisabled"
      >
        <BittsTag
          class="flex"
          text="No data shared"
          color="warning"
          size="x-small"
        />
        <template #title>This partner doesn't have any populations </template>
      </BittsTooltip>
      <BittsTooltip
        button-text="Request data"
        :include-cta="true"
        @cta-clicked="redirectToPartnerFromPopId(Number(node.id))"
        placement="bottom"
        v-if="
          isPopulationNode(node) &&
          populationIdsSharingOnlyCount.has(Number(node.id))
        "
      >
        <BittsTag
          class="flex"
          text="No data shared"
          color="warning"
          size="x-small"
        />
        <template #title
          >This partner is not sharing data from this population with you
        </template>
      </BittsTooltip>
      <BittsTag
        v-if="isPopulationNode(node) && isStandardPopulation(Number(node.id))"
        text="Standard"
        color="info"
        size="x-small"
      />
      <BittsButton
        @click.stop="resetToDefault(String(node.id))"
        :left-icon="['fak', 'cycle']"
        left-icon-classes="text-neutral-accent"
        size="x-small"
        type="neutral"
        variant="ghost"
        text="Reset to default"
        v-if="
          isPartnerOrgNode(node) &&
          node.children?.length !== 0 &&
          partnerIdsWithOverrides.has(String(node.id))
        "
      />
    </template>
  </BittsTreeWithSearch>
</template>

<script setup lang="ts">
import { BittsButton, BittsTag, BittsTreeWithSearch } from '@crossbeam/bitts';
import BittsTooltip from '@crossbeam/bitts/src/BittsTooltip/BittsTooltip.vue';
import { TreeNodeType } from '@crossbeam/bitts/src/BittsTree/BittsBaseTree.vue';

import { groupBy, sortBy } from 'lodash';
import { computed } from 'vue';
import { useRouter } from 'vue-router';

import OfflineManagedPartnerLabel from '@/components/partners/OfflineManagedPartnerLabel.vue';

import { FEATURE_FLAG } from '@/constants/feature_flags';
import { STANDARD_POPULATION_TYPES } from '@/constants/standard_populations';
import {
  useDataSharesStore,
  useFeatureFlagStore,
  usePartnersStore,
  usePopulationsStore,
} from '@/stores';
import { MinimalIntegrationProfile } from '@/types/integration_profiles';
import { Partner } from '@/types/partners';
import {
  ALL_SEGMENTS,
  PartnerPopulation,
  SEGMENTS_TO_LABEL_MAP,
  SegmentType,
} from '@/types/populations';

const props = withDefaults(
  defineProps<{
    integrationProfileSettings?: MinimalIntegrationProfile | null;
    isDisabled?: boolean;
    settingType: string;
    hideOfflinePartners?: boolean;
  }>(),
  {
    integrationProfileSettings: undefined,
    isDisabled: false,
    hideOfflinePartners: false,
  },
);
const emit =
  defineEmits<
    (e: 'update-settings', payload: MinimalIntegrationProfile) => void
  >();
const router = useRouter();
const populationsStore = usePopulationsStore();
const partnersStore = usePartnersStore();
const dataShareStore = useDataSharesStore();
const featureFlagStore = useFeatureFlagStore();

const hasSegmentGrouping = featureFlagStore.hasFeatureFlag(
  FEATURE_FLAG.TEMP_UPDATED_DATA_PUSH_POP_TYPES,
);
const checkedKeys = computed(
  () =>
    new Set(
      props.integrationProfileSettings?.population_settings
        .filter((pop) => pop.is_partner_population && pop.is_included)
        .map((pop) => pop.population_id),
    ),
);
const checkedStandardKeys = computed(
  () =>
    new Set(
      props.integrationProfileSettings?.standard_population_settings
        .filter((pop) => pop.is_included)
        .map((pop) => pop.standard_type),
    ),
);
const partnerPopulationsByOrgId = computed(() =>
  groupBy(populationsStore.partnerPopulations, 'organization_id'),
);
const populationSettingIds = computed(
  () =>
    new Set(
      props.integrationProfileSettings?.population_settings.map(
        (pop) => pop.population_id,
      ),
    ),
);
const partnerIdsWithOverrides = computed(
  () =>
    new Set(
      partnersStore.partnerOrgs
        .filter((partner) =>
          partnerPopulationsByOrgId.value[partner.id]?.some((pop) =>
            populationSettingIds.value.has(pop.id),
          ),
        )
        .map((partner) => partner.uuid),
    ),
);
const populationIdsSharingOnlyCount = computed(
  () =>
    new Set(
      populationsStore.partnerPopulations
        .filter((pop) => isSharingOnlyCount(pop.id))
        .map((pop) => pop.id),
    ),
);

const filterForCustomersAndOppsPopulations = (pop: PartnerPopulation) =>
  pop.standard_type === 'open_opportunities' ||
  pop.standard_type === 'customers';

const treeData = computed<TreeNodeType[]>(() => {
  const partners = props.hideOfflinePartners
    ? partnersStore.partnerOrgs.filter(
        (partner) => !partner.offline_partner && !partner.virtual_partner,
      )
    : partnersStore.partnerOrgs;

  return partners.map((partner) => {
    const hasOverrides = partnerIdsWithOverrides.value.has(partner.uuid);

    const orgPopulations = partnerPopulationsByOrgId.value[partner.id] ?? [];
    const partnerPopulations =
      props.settingType === 'clari'
        ? orgPopulations.filter(filterForCustomersAndOppsPopulations)
        : orgPopulations;

    if (!hasSegmentGrouping) {
      const children = sortBy(partnerPopulations, (pop) => {
        if (pop.standard_type)
          return STANDARD_POPULATION_TYPES.indexOf(pop.standard_type);
      }).map((pop) => ({
        label: pop.name,
        id: pop.id,
        checked: hasOverrides
          ? checkedKeys.value.has(pop.id)
          : pop.standard_type
            ? checkedStandardKeys.value.has(pop.standard_type)
            : checkedStandardKeys.value.has('custom'),
        children: [],
      }));

      return {
        isDisabled: children.length === 0,
        label: partner.name,
        id: partner.uuid,
        checked:
          children.length > 0 && children.every((child) => child.checked),
        children,
      };
    }

    const populationsBySegment = new Map<SegmentType, PartnerPopulation[]>();

    partnerPopulations.forEach((pop) => {
      const segmentType = pop.segment as SegmentType;
      if (!populationsBySegment.has(segmentType)) {
        populationsBySegment.set(segmentType, []);
      }
      populationsBySegment.get(segmentType)?.push(pop);
    });

    const segmentNodes: TreeNodeType[] = ALL_SEGMENTS.filter((segmentType) => {
      const populations = populationsBySegment.get(segmentType);
      return populations && populations.length > 0;
    }).map((segmentType) => {
      const populations = populationsBySegment.get(segmentType) ?? [];

      const populationNodes = sortBy(populations, (pop) => {
        if (pop.standard_type)
          return STANDARD_POPULATION_TYPES.indexOf(pop.standard_type);
      }).map((pop) => ({
        label: pop.name,
        id: pop.id,
        checked: hasOverrides
          ? checkedKeys.value.has(pop.id)
          : segmentType === 'other'
            ? checkedStandardKeys.value.has('custom')
            : checkedStandardKeys.value.has(segmentType),
        children: [],
      }));

      return {
        label: SEGMENTS_TO_LABEL_MAP[segmentType],
        id: `${partner.uuid}-${segmentType}`,
        checked: populationNodes.every((child) => child.checked),
        children: populationNodes,
      };
    });

    return {
      isDisabled: segmentNodes.length === 0,
      label: partner.name,
      id: partner.uuid,
      checked:
        segmentNodes.length > 0 && segmentNodes.every((child) => child.checked),
      children: segmentNodes,
    };
  });
});

const isSharingOnlyCount = (partnerPopulationId: number) => {
  return (
    (dataShareStore.getIncomingSharingRules({
      partnerPopulationId,
      isOverride: null,
    })?.[0]?.shared_fields.length ?? 0) === 0
  );
};

const isPartnerOrgNode = (node: TreeNodeType) => {
  if (typeof node.id !== 'string') {
    return false;
  }

  if (!hasSegmentGrouping) {
    return true;
  }

  return !!partnersStore.getPartnerOrgByUuid(node.id);
};

const isPopulationNode = (node: TreeNodeType) => {
  return typeof node.id === 'number';
};

const populationsById = computed(
  () =>
    new Map(populationsStore.partnerPopulations.map((pop) => [pop.id, pop])),
);
const isStandardPopulation = (populationId: number) => {
  const population = populationsById.value.get(populationId);
  if (!population) {
    return false;
  }
  return population.standard_type !== null;
};

const isOfflinePartner = (partnerId: number | string) => {
  return typeof partnerId === 'number'
    ? false
    : (partnersStore.getPartnerOrgByUuid(partnerId)?.offline_partner ?? false);
};

const isManagedOfflinePartner = (partnerId: number | string) => {
  return typeof partnerId === 'number'
    ? false
    : (partnersStore.getPartnerOrgByUuid(partnerId)?.virtual_partner ?? false);
};

function getUpdatedPopulationsWithoutSegmentGrouping(
  node: TreeNodeType[],
  updatedNodeId: number | string,
): TreeNodeType[] {
  const updatedPops: TreeNodeType[] = [];

  for (const partner of node) {
    if (partner.id === updatedNodeId) {
      updatedPops.push(...(partner.children ?? []));
      break;
    }

    const child = partner.children?.find((pop) => pop.id === updatedNodeId);
    if (!child) continue;

    const hasOverrides = partnerIdsWithOverrides.value.has(
      partner.id as string,
    );
    hasOverrides
      ? updatedPops.push(child)
      : updatedPops.push(...(partner.children ?? []));
    break;
  }

  return updatedPops;
}

function getUpdatedPopulationsWithSegmentGrouping(
  node: TreeNodeType[],
  updatedNodeId: number | string,
): TreeNodeType[] {
  const updatedPops: TreeNodeType[] = [];

  for (const partner of node) {
    if (partner.id === updatedNodeId) {
      for (const segment of partner.children ?? []) {
        updatedPops.push(...(segment.children ?? []));
      }
      break;
    }

    const segment = partner.children?.find((seg) => seg.id === updatedNodeId);
    if (segment) {
      const hasOverrides = partnerIdsWithOverrides.value.has(
        partner.id as string,
      );

      if (hasOverrides) {
        updatedPops.push(...(segment.children ?? []));
      } else {
        for (const seg of partner.children ?? []) {
          updatedPops.push(...(seg.children ?? []));
        }
      }
      break;
    }

    for (const segment of partner.children ?? []) {
      const population = segment.children?.find(
        (pop) => pop.id === updatedNodeId,
      );
      if (!population) continue;

      const hasOverrides = partnerIdsWithOverrides.value.has(
        partner.id as string,
      );

      if (hasOverrides) {
        updatedPops.push(population);
      } else {
        for (const seg of partner.children ?? []) {
          updatedPops.push(...(seg.children ?? []));
        }
      }
      break;
    }

    if (updatedPops.length > 0) break;
  }

  return updatedPops;
}

const onUpdate = (node: TreeNodeType[], updatedNodeId: number | string) => {
  const updatedPops = hasSegmentGrouping
    ? getUpdatedPopulationsWithSegmentGrouping(node, updatedNodeId)
    : getUpdatedPopulationsWithoutSegmentGrouping(node, updatedNodeId);

  const updatedSettings: MinimalIntegrationProfile = {
    population_settings: [
      ...(props.integrationProfileSettings?.population_settings ?? []).filter(
        (pop) => !updatedPops.some((p) => p.id === pop.population_id),
      ),
      ...updatedPops.map((pop) => ({
        is_included: pop.checked ?? false,
        is_partner_population: true,
        population_id: pop.id as number,
        config_type: props.settingType,
      })),
    ],
    standard_population_settings:
      props.integrationProfileSettings?.standard_population_settings ?? [],
  };

  emit('update-settings', updatedSettings);
};

const redirectToPartnerFromPopId = (populationId: number) => {
  const partnerId = populationsStore.partnerPopulations.find(
    (pop) => pop.id === populationId,
  )?.organization_id;
  if (!partnerId) {
    return;
  }
  router.push({
    name: 'partnership-settings',
    params: {
      partner_org_id: partnerId,
      tab: 'shared-with-you',
    },
  });
};

const resetToDefault = (partnerUuid: string) => {
  const partnerId = partnersStore.getPartnerOrgByUuid(partnerUuid)?.id;
  const populationsToRemove = populationsStore.partnerPopulations.filter(
    (pop) => pop.organization_id === partnerId,
  );
  const updatedSettings: MinimalIntegrationProfile = {
    population_settings: [
      ...(props.integrationProfileSettings?.population_settings ?? []).filter(
        (pop) => !populationsToRemove.some((p) => p.id === pop.population_id),
      ),
    ],
    standard_population_settings:
      props.integrationProfileSettings?.standard_population_settings ?? [],
  };

  emit('update-settings', updatedSettings);
};
</script>
